import { t } from "i18next";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";

import Icons from "@app/assets/Icons";
import LoadingButton from "@app/components/Button";
import Loader from "@app/components/Loader";
import Pagination from "@app/components/Pagination";
import TableHeader from "@app/components/TableHeader";
import ViewUtilityHeader from "@app/components/ViewUtilityHeader";
import AlertModal from "@app/containers/alertModal";
import {
    getAlertCount,
    getAlertDetails,
    updateAlertStatus,
} from "@app/services/Endpoints/Alert.API";
import { Alerts, AlertSortDirection, AlertType } from "@app/types/Alert.types";
import { HeadersMapInterface } from "@app/types/Common.types";
import { AppContext } from "@app/utils/contexts/App.context";
import { formatTimeHHMM } from "@app/utils/timeHelpers";

import "./index.scss";

let totalPages = 0;

const AlertsPage: FC = () => {
    const [alerts, setAlerts] = useState<Alerts[]>([]);
    const [sortByObj, setSortByObj] = useState({
        sortBy: "Time",
        sortOrder: AlertSortDirection.Descending,
    });
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [loader, setLoader] = useState<boolean>(false);
    const [searchVal, setSearchVal] = useState<string>("");
    const [showModal, setShowModal] = useState<boolean>(false);
    const [selectedAlert, setSelectedAlert] = useState<string | null>(null);
    const { appUser, setAlertCount } = useContext(AppContext);

    const HeadersMap: HeadersMapInterface = {
        BranchCode: {
            label: t("VIEW.ALERTS.BRANCHCODE"),
            layout: { lg: 2, sm: 2 },
        },
        BranchName: { label: t("VIEW.ALERTS.BRANCH"), layout: { lg: 2, sm: 2 } },
        BranchType: {
            label: t("VIEW.ALERTS.TYPE"),
            layout: { lg: 2, sm: 2 },
            disableSort: true,
        },
        Details: {
            label: t("VIEW.ALERTS.DETAILS"),
            layout: { lg: 3, sm: 3 },
            disableSort: true,
        },
        Time: {
            label: t("VIEW.ALERTS.TIME"),
            layout: { lg: 1, sm: 1 },
        },
        Status: { label: t("VIEW.ALERTS.STATUS"), layout: { lg: 2, sm: 2 }, disableSort: true },
    };

    const fetchAlerts = useCallback(() => {
        setLoader(true);
        getAlertDetails(
            appUser?.licenseeID,
            pageNumber,
            pageSize,
            searchVal,
            sortByObj.sortBy,
            sortByObj.sortOrder,
        )
            .then((response) => {
                if (response) {
                    totalPages = response?.data?.totalPages;
                    setAlerts(response?.data?.data || []);
                    setLoader(false);
                }
            })
            .catch(() => {
                setAlerts([]);
                setLoader(false);
            });
    }, [
        appUser?.licenseeID,
        pageNumber,
        pageSize,
        searchVal,
        sortByObj.sortBy,
        sortByObj.sortOrder,
    ]);

    const fetchAlertsCount = useCallback(() => {
        getAlertCount(appUser?.licenseeID)
            .then((response) => {
                if (response?.data) {
                    setAlertCount(response?.data?.data);
                }
            })
            .catch(() => {
                setAlertCount(0);
            });
    }, [appUser?.licenseeID, setAlertCount]);

    useEffect(() => {
        fetchAlerts();
    }, [fetchAlerts]);

    const sortByHandler = (headerKey: string) => {
        setSortByObj({ sortBy: headerKey, sortOrder: Number(!sortByObj.sortOrder) });
    };

    const onChangeRecordsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPageSize(Number(event?.target?.value));
        setPageNumber(1);
    };

    const onPageChange = (pageNumber: number) => {
        setPageNumber(Number(pageNumber));
    };

    const handleSearch = (val: string) => {
        setSearchVal(val);
    };

    const rightArrowIcon = <img src={Icons.RightIcon} className='alert-btn__rightIcon' />;

    const getAlertStatusButton = (alert: Alerts) => {
        const config: Record<number, JSX.Element | null> = {
            0:
                alert.alertType === AlertType.Delay ? (
                    <LoadingButton
                        btnClass='alert-btn alert-btn__mark_attended_yellow'
                        onClick={() => {
                            setSelectedAlert(alert.id);
                            setShowModal(true);
                        }}
                        label={t("COMMON.MARKATTENDED")}
                        variant='submit'
                    />
                ) : null,
            1: (
                <LoadingButton
                    icon={rightArrowIcon}
                    btnClass='alert-btn alert-btn__attended'
                    label={t("COMMON.ATTENDED")}
                    variant='submit'
                    disabled
                />
            ),
        };

        return config[alert.status] ?? null;
    };

    const getRowClassName = (item: Alerts): string => {
        if (item.status === 1) return "alerts__rowAttendedGrey";
        if (item.alertType === AlertType.Delay) return "alerts__rowDelayYellow";
        return "";
    };

    const submitAlert = () => {
        if (!selectedAlert) return;

        const alert = alerts.find((item) => item.id === selectedAlert);
        if (!alert) return;

        updateAlertStatus(alert.licenseeId, alert.id, alert.branchCode, 1)
            .then((res) => {
                if (res?.data) {
                    fetchAlerts();
                    fetchAlertsCount();
                }
            })
            .catch(() => {
                setSelectedAlert(null);
            });
    };

    return (
        <Container fluid className='branches'>
            <ViewUtilityHeader
                onChange={onChangeRecordsPerPage}
                placeholder='Search for Branch'
                btnLabel={t("VIEW.BRANCHES.ADDBRANCHES")}
                onSearch={handleSearch}
            />

            <TableHeader
                headersMap={HeadersMap}
                enableIconColumn={false}
                sortByHandler={sortByHandler}
                sortByObj={sortByObj}
            />

            <div className='alerts_page'>
                {loader && <Loader />}

                {!loader &&
                    alerts.length > 0 &&
                    alerts.map((item, index) => (
                        <Row key={index} className={`alerts__rowWrap ${getRowClassName(item)}`}>
                            <Col key={item.branchCode} className='alerts--pl20' sm='2' lg='2'>
                                {item.branchCode}
                            </Col>
                            <Col key={item.branchName} sm='2' lg='2'>
                                {item.branchName}
                            </Col>
                            <Col key={item.alertType} sm='2' lg='2'>
                                {AlertType[item.alertType]}
                            </Col>
                            <Col key={item.alertDescription} sm='3' lg='3'>
                                {item.alertDescription}
                            </Col>
                            <Col key={item.alertTime} sm='1' lg='1'>
                                {formatTimeHHMM(item.alertTime)}
                            </Col>
                            <Col key={item.status} sm='2' lg='2'>
                                {getAlertStatusButton(item)}
                            </Col>
                        </Row>
                    ))}

                {!loader && alerts.length === 0 && (
                    <div className='alerts--noData'>{t("APIMSG.NOALERTS")}</div>
                )}
            </div>

            <Pagination
                sortByObj={sortByObj}
                onPageChange={onPageChange}
                totalPages={totalPages}
                pageSize={pageSize}
                pageNumber={pageNumber}
            />

            <AlertModal
                showModal={showModal}
                setShowModal={setShowModal}
                submitAlert={submitAlert}
            />
        </Container>
    );
};

export default AlertsPage;
